import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles, useMediaQuery } from '@material-ui/core';
import { useLocation } from 'react-router';
import Breadcrumbs from 'components/Dashboard/Breadcrumbs';
import Loader from 'components/Shared/Loader';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectBreadcrumbs,
  toggleIsInfoPaneExpanded,
  toggleIsSideMenuPinned,
} from '../../../redux_store';
import cx from 'classnames';
import NavBar from './Navigation/NavSection/NavBar';
import BlurScreen from '../../Shared/BlurScreen';
import InfoPane from '../../InfoPane/InfoPane';
import TopBar from './Navigation/TopBar/TopBar';
import { INFO_PANE_SIZES } from '../../InfoPane/constants';
import theme from '../../../config/theme';

const useStyles = makeStyles((theme) => ({
  dashboard: {
    height: '100%',
    background: `linear-gradient(180deg, ${theme.colors.blue} 0%, ${theme.colors.lightBlue} 100%)`,
  },
  dashboardInner: {
    width: '100%',
    transition: 'width 0.3s',
  },
  content: {
    height: 'calc(100% - 48px)', // 112 = topBar 80 + breadcrumbs 48
    paddingRight: theme.spacing(3),
    transition: '0.3s',
  },
  contentSmallPadding: {
    paddingRight: theme.spacing(.8),
    transition: '0.3s',
  },
  blurDashboard: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 1000,
  },
  hideBlur: {
    height: 0,
  },
}));

const Dashboard = ({ children }) => {
  const classes = useStyles();
  const breadcrumbs = useSelector(selectBreadcrumbs) || [];
  const { state } = useLocation();
  const delay = state ? state.delay : 0;
  const [delayed, setDelayed] = useState(!!delay);
  const { isSideMenuPinned } = useSelector((state) => state.appSettings);
  const { isInfoPaneExpanded, currentPage } = useSelector((state) => state.infoPane);
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  /*
    Set a delay (provides from AppRouteProvider)./
    Cases: create a new list item.
  */
  useEffect(() => {
    if (delayed) {
      setTimeout(() => {
        setDelayed(false);
      }, delay);
    }
  }, []);

  return breadcrumbs.length
    ? <BlurScreen>
      <Grid container wrap='nowrap' className={classes.dashboard}>
        <NavBar />
        <TopBar />
        <div className={classes.dashboardInner} id={'dashboardInner'}
             style={{
               width: `calc(100% - ${isSideMenuPinned ? 240 : 70}px - ${isInfoPaneExpanded && currentPage?.uuid && isDesktop ? INFO_PANE_SIZES.full_width : currentPage?.uuid ? INFO_PANE_SIZES.small_width : 0}px)`,
               transition: '0.3s',
               position: 'relative',
             }}
        >
          {/* Overlay to catch clicks and darken area when info pane is expanded */}
          {isInfoPaneExpanded && !isDesktop && <LaptopNoClickableArea/>}
          <div style={{ paddingTop: 80, width: '100%', height: '100%' }}>
            <Breadcrumbs />
            <Grid container className={cx(classes.content, {
              [classes.contentSmallPadding]: currentPage?.uuid,
            })}>
              {
                delayed
                  ? <ContentWrapper>
                    <Loader fullHeight />
                  </ContentWrapper>
                  : children
              }
            </Grid>
          </div>
        </div>
        <InfoPane />
      </Grid>
    </BlurScreen>
    : <Loader fullHeight />;
};

Dashboard.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default Dashboard;

const LaptopNoClickableArea = ({ children }) => {
  const dispatch = useDispatch();
  return <div
    style={{
      position: 'absolute',
      top: 80,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 10,
      cursor: 'pointer',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(2px)',
    }}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      dispatch(toggleIsInfoPaneExpanded(false));
    }}
  />
}

